import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Globe, Save, Edit3, MousePointer, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const SUPPORTED_LANGS = ['en', 'de', 'hr', 'es', 'fr', 'it', 'pt', 'ru', 'zh', 'nl', 'sl'];
const LANG_NAMES = {
  en: 'English', de: 'German', hr: 'Croatian', es: 'Spanish',
  fr: 'French', it: 'Italian', pt: 'Portuguese', ru: 'Russian',
  zh: 'Chinese', nl: 'Dutch', sl: 'Slovenian',
};

export default function InlineContentEditor() {
  const [active, setActive] = useState(false);
  const [selectedElement, setSelectedElement] = useState(null);
  const [selectedPath, setSelectedPath] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editLang, setEditLang] = useState('en');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [hoveredEl, setHoveredEl] = useState(null);
  const overlayRef = useRef(null);

  const getElementPath = useCallback((el) => {
    if (el.dataset?.contentId) return el.dataset.contentId;
    const parts = [];
    let current = el;
    while (current && current !== document.body) {
      let selector = current.tagName.toLowerCase();
      if (current.id) {
        selector += `#${current.id}`;
        parts.unshift(selector);
        break;
      }
      if (current.dataset?.testid) {
        selector += `[data-testid="${current.dataset.testid}"]`;
        parts.unshift(selector);
        break;
      }
      if (current.className && typeof current.className === 'string') {
        const classes = current.className.split(' ')
          .filter(c => c && !c.includes('hover') && !c.includes('active') && !c.includes('/') && !c.includes('[') && !c.includes(']'))
          .slice(0, 2).join('.');
        if (classes) selector += `.${classes}`;
      }
      const parent = current.parentElement;
      if (parent) {
        const siblings = Array.from(parent.children).filter(c => c.tagName === current.tagName);
        if (siblings.length > 1) {
          const idx = siblings.indexOf(current);
          selector += `:nth-child(${idx + 1})`;
        }
      }
      parts.unshift(selector);
      current = current.parentElement;
    }
    return parts.join(' > ');
  }, []);

  const handleClick = useCallback((e) => {
    if (!active) return;
    // Ignore clicks on the editor toolbar itself
    if (overlayRef.current && overlayRef.current.contains(e.target)) return;
    e.preventDefault();
    e.stopPropagation();

    const el = e.target;
    if (!el.textContent?.trim()) return;
    // Only allow editing leaf text elements (no child elements) to prevent crash
    if (el.children && el.children.length > 0) return;

    setSelectedElement(el);
    setSelectedPath(getElementPath(el));
    setEditContent(el.textContent.trim());
    setSaved(false);
  }, [active, getElementPath]);

  const handleMouseOver = useCallback((e) => {
    if (!active) return;
    if (overlayRef.current && overlayRef.current.contains(e.target)) return;
    e.target.style.outline = '2px solid rgba(32, 192, 181, 0.5)';
    e.target.style.outlineOffset = '2px';
    setHoveredEl(e.target);
  }, [active]);

  const handleMouseOut = useCallback((e) => {
    if (!active) return;
    e.target.style.outline = '';
    e.target.style.outlineOffset = '';
    setHoveredEl(null);
  }, [active]);

  useEffect(() => {
    if (active) {
      document.addEventListener('click', handleClick, true);
      document.addEventListener('mouseover', handleMouseOver, true);
      document.addEventListener('mouseout', handleMouseOut, true);
    }
    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('mouseover', handleMouseOver, true);
      document.removeEventListener('mouseout', handleMouseOut, true);
      // Clean up outlines
      if (hoveredEl) {
        hoveredEl.style.outline = '';
        hoveredEl.style.outlineOffset = '';
      }
    };
  }, [active, handleClick, handleMouseOver, handleMouseOut, hoveredEl]);

  const handleSave = async () => {
    if (!selectedPath || !editContent) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API}/api/content-editor/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          element_path: selectedPath,
          language: editLang,
          content: editContent,
          page_url: window.location.pathname,
        }),
      });
      if (res.ok) {
        setSaved(true);
        // Safely update element text without breaking React reconciliation
        if (selectedElement) {
          if (selectedElement.childNodes.length === 1 && selectedElement.childNodes[0].nodeType === 3) {
            selectedElement.childNodes[0].nodeValue = editContent;
          } else if (selectedElement.childNodes.length === 0) {
            selectedElement.textContent = editContent;
          }
        }
        setTimeout(() => setSaved(false), 2000);
      }
    } catch (err) {
      console.error('Save failed:', err);
    }
    setSaving(false);
  };

  // Load edits for current page/language on mount
  useEffect(() => {
    const loadEdits = async () => {
      try {
        const res = await fetch(`${API}/api/content-editor/edits?page_url=${window.location.pathname}&language=${editLang}`);
        if (res.ok) {
          const data = await res.json();
          (data.edits || []).forEach(edit => {
            try {
              const el = document.querySelector(edit.element_path);
              if (el) {
                if (el.childNodes.length === 1 && el.childNodes[0].nodeType === 3) {
                  el.childNodes[0].nodeValue = edit.content;
                } else if (el.childNodes.length === 0) {
                  el.textContent = edit.content;
                }
              }
            } catch {}
          });
        }
      } catch {}
    };
    loadEdits();
  }, [editLang]);

  return (
    <div ref={overlayRef} data-testid="inline-content-editor">
      {/* Toggle Button */}
      <button
        onClick={() => { setActive(!active); if (active) { setSelectedElement(null); setSelectedPath(''); } }}
        className={`fixed bottom-20 right-4 z-[200] p-3 rounded-full shadow-2xl transition-all ${
          active ? 'bg-cyan-500 text-white shadow-cyan-500/30 scale-110' : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-white/10'
        }`}
        data-testid="content-editor-toggle"
        title={active ? 'Exit Edit Mode' : 'Edit Page Content'}
      >
        {active ? <X size={20} /> : <Edit3 size={20} />}
      </button>

      {/* Active Indicator */}
      {active && (
        <div className="fixed top-2 left-1/2 -translate-x-1/2 z-[200] px-4 py-2 bg-cyan-500/90 text-white text-xs font-bold rounded-full flex items-center gap-2 backdrop-blur-sm shadow-xl animate-pulse"
          data-testid="editor-active-indicator">
          <MousePointer size={14} /> EDIT MODE — Click any text to edit
        </div>
      )}

      {/* Editor Panel */}
      {active && selectedElement && (
        <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-[200] bg-[#0C1220] border border-cyan-500/30 rounded-xl shadow-2xl shadow-cyan-500/10 p-4"
          data-testid="content-edit-panel">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-bold text-white flex items-center gap-2">
              <Edit3 size={14} className="text-cyan-400" /> Edit Content
            </h4>
            <button onClick={() => { setSelectedElement(null); setSelectedPath(''); }} className="text-gray-400 hover:text-white">
              <X size={14} />
            </button>
          </div>

          <div className="mb-3">
            <label className="text-[10px] text-gray-500 uppercase tracking-wider mb-1 block">Language</label>
            <div className="flex flex-wrap gap-1">
              {SUPPORTED_LANGS.map(l => (
                <button key={l} onClick={() => setEditLang(l)}
                  className={`px-2 py-1 rounded text-[10px] font-medium transition-all ${
                    editLang === l ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 'bg-white/5 text-gray-400 hover:text-white border border-transparent'
                  }`}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-3">
            <label className="text-[10px] text-gray-500 uppercase tracking-wider mb-1 block">Element Path</label>
            <p className="text-[10px] text-gray-500 bg-black/30 rounded px-2 py-1 font-mono break-all">{selectedPath}</p>
          </div>

          <textarea
            value={editContent}
            onChange={e => setEditContent(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-sm text-white focus:border-cyan-500 focus:outline-none resize-none mb-3"
            data-testid="content-edit-textarea"
          />

          <button onClick={handleSave} disabled={saving}
            className="w-full py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-sm font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="content-edit-save-btn">
            {saved ? <><Check size={14} /> Saved!</> : saving ? 'Saving...' : <><Save size={14} /> Save for {LANG_NAMES[editLang]}</>}
          </button>
        </div>
      )}
    </div>
  );
}
